import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, Typography, Stack, Box, Accordion, AccordionSummary, AccordionDetails, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, Paper, TableContainer } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import OneCommTransactionsContent from './OneCommTransactionsContent';
import { formatMoney } from '../ProducerDashboardHelper';
import { OneCommTotalPendingTransactions } from '../../interfaces/ProducerDashboard';
import { TRANSACTION_TYPES_CONFIG } from './TransactionConfig';

interface Props {
    open: boolean;
    onClose: () => void;
    data: OneCommTotalPendingTransactions;
    currency: string;
    title: string;
    subtitle?: string;
}

export default function OneCommTransactionsModal({ open, onClose, data, currency, title, subtitle }: Props) {
    const [statsExpanded, setStatsExpanded] = useState(false);

    const presentTypes = Object.keys(data.pending_transactions);
    const escrowTypes = presentTypes.filter(type => TRANSACTION_TYPES_CONFIG[type]?.requireTransferProducer || TRANSACTION_TYPES_CONFIG[type]?.requireTransferOperatorFromEscrow);
    const walletTypes = presentTypes.filter(type => TRANSACTION_TYPES_CONFIG[type]?.requireTransferOperatorFromProducer);

    const cellStyle = { fontSize: '0.65rem', py: 0.5, borderBottom: '1px solid #f0f0f0' };
    const headerStyle = { ...cellStyle, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', bgcolor: '#fafafa' };
    const footerStyle = { ...cellStyle, fontWeight: 800, bgcolor: '#fafafa', color: 'text.primary', borderTop: '1px solid #e0e0e0' };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '95vh', borderRadius: '4px' } }}>
            <DialogTitle sx={{ p: 1.5, borderBottom: '1px solid #eee' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>{title}</Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.55rem', fontWeight: 700 }}>
                            {subtitle || 'ANALYTICAL SUMMARY'}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} size="small"><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                <Accordion
                    expanded={statsExpanded}
                    onChange={() => setStatsExpanded(!statsExpanded)}
                    sx={{ boxShadow: 'none', '&:before': { display: 'none' }, borderBottom: '1px solid #eee' }}
                >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 16 }} />} sx={{ minHeight: 40, px: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                            <AnalyticsIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>VIEW ANALYTICS</Typography>
                        </Stack>

                        <Stack direction="row" spacing={4} sx={{ mr: 4 }}>
                            {/*  PRODUCER */}
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.5rem', fontWeight: 700, display: 'block' }}>TOTAL PENDING PRODUCER</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#2e7d32' }}>
                                    {formatMoney(data.total_pending_from_escrow_for_producer, currency)}
                                </Typography>
                            </Box>

                            {/* OPERATOR */}
                            <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.5rem', fontWeight: 700, display: 'block' }}>TOTAL PENDING OPERATOR</Typography>
                                <Typography sx={{ fontWeight: 800, fontSize: '0.75rem', color: '#1976d2', lineHeight: 1.1 }}>
                                    {formatMoney(data.total_pending_from_escrow_for_operator + data.total_pending_from_producer_for_operator, currency)}
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                    <Typography variant="caption" sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600 }}>
                                        Escrow: {formatMoney(data.total_pending_from_escrow_for_operator, currency)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '0.45rem', color: 'text.disabled', fontWeight: 600 }}>
                                        Producer Wallet: {formatMoney(data.total_pending_from_producer_for_operator, currency)}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Stack>
                    </AccordionSummary>

                    <AccordionDetails sx={{ px: 2, pb: 2, bgcolor: '#fcfcfc' }}>
                        <Stack direction="row" spacing={2}>
                            {/* TABLE 1: ESCROW */}
                            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1.2, borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerStyle}>Escrow Transfers</TableCell>
                                            <TableCell align="right" sx={headerStyle}>To Producer</TableCell>
                                            <TableCell align="right" sx={headerStyle}>To Operator</TableCell>
                                            <TableCell align="center" sx={headerStyle}>Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {escrowTypes.map(type => (
                                            <TableRow key={type}>
                                                <TableCell sx={cellStyle}>{TRANSACTION_TYPES_CONFIG[type]?.label}</TableCell>
                                                <TableCell align="right" sx={cellStyle}>{TRANSACTION_TYPES_CONFIG[type]?.requireTransferProducer ? formatMoney(data.pending_transactions[type].pending_from_escrow_for_producer, currency) : '—'}</TableCell>
                                                <TableCell align="right" sx={cellStyle}>{TRANSACTION_TYPES_CONFIG[type]?.requireTransferOperatorFromEscrow ? formatMoney(data.pending_transactions[type].pending_from_escrow_for_operator, currency) : '—'}</TableCell>
                                                <TableCell align="center" sx={{ ...cellStyle, color: 'text.disabled' }}>{data.pending_transactions[type].count_by_type}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell sx={footerStyle}>TOTAL</TableCell>
                                            <TableCell align="right" sx={footerStyle}>{formatMoney(data.total_pending_from_escrow_for_producer, currency)}</TableCell>
                                            <TableCell align="right" sx={footerStyle}>{formatMoney(data.total_pending_from_escrow_for_operator, currency)}</TableCell>
                                            <TableCell align="center" sx={footerStyle}>{escrowTypes.reduce((acc, t) => acc + data.pending_transactions[t].count_by_type, 0)}</TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>

                            {/* TABLE 2: WALLET */}
                            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, borderRadius: 1 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={headerStyle}>From Producer → OP</TableCell>
                                            <TableCell align="right" sx={headerStyle}>Amount</TableCell>
                                            <TableCell align="center" sx={headerStyle}>Qty</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {walletTypes.map(type => (
                                            <TableRow key={type}>
                                                <TableCell sx={cellStyle}>{TRANSACTION_TYPES_CONFIG[type]?.label}</TableCell>
                                                <TableCell align="right" sx={{ ...cellStyle, fontWeight: 600 }}>{formatMoney(data.pending_transactions[type].pending_from_producer_for_operator, currency)}</TableCell>
                                                <TableCell align="center" sx={{ ...cellStyle, color: 'text.disabled' }}>{data.pending_transactions[type].count_by_type}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow>
                                            <TableCell sx={footerStyle}>TOTAL</TableCell>
                                            <TableCell align="right" sx={footerStyle}>{formatMoney(data.total_pending_from_producer_for_operator, currency)}</TableCell>
                                            <TableCell align="center" sx={footerStyle}>{walletTypes.reduce((acc, t) => acc + data.pending_transactions[t].count_by_type, 0)}</TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                            </TableContainer>
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                    <OneCommTransactionsContent data={data} currency={currency} />
                </Box>
            </DialogContent>
        </Dialog>
    );
}